import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigProvider, theme, Alert, Button, Card, Form, InputNumber, message, Modal, Popconfirm, Select, Space, Table, Typography } from 'antd';
import { useAuth } from '../../context/DataContext';
import { orchestraAdmin } from './OrchestraAssignments';

export default function OrchestraSettings() {
    const { currentEventId } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const eventIdRef = useRef(currentEventId);
    eventIdRef.current = currentEventId;
    const requestRef = useRef(0);
    const venueId = Form.useWatch('venue', form);
    const date = Form.useWatch('date', form);
    const load = useCallback(async () => {
        if (!currentEventId || currentEventId !== eventIdRef.current) return;
        const request = ++requestRef.current;
        const result = await orchestraAdmin('sessions', { eventId: currentEventId });
        if (request === requestRef.current && currentEventId === eventIdRef.current) setEvent(result);
    }, [currentEventId]);
    useEffect(() => {
        setEvent(null); setOpen(false);
        load().catch(error => message.error(error.message));
        return () => { requestRef.current += 1; };
    }, [load]);
    const update = async payload => {
        setLoading(true);
        try { await orchestraAdmin('session', { eventId: currentEventId, ...payload }); await load(); setOpen(false); message.success('Session saved.'); }
        catch (error) { message.error(error.message); }
        finally { setLoading(false); }
    };
    const venues = event?.venues || [];
    const selectedVenue = venues.find(venue => venue.id === venueId);
    return <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: { colorPrimary: '#EBBC64', colorBgContainer: '#1E1E1E', colorText: '#e5cc92' } }}><Card style={{ margin: 24, background: '#121212', color: '#e5cc92' }}>
        <Typography.Title level={3}>Orchestra Settings</Typography.Title>
        <Alert showIcon type="info" message="Orchestra uses free seating"
            description="Set the performance-linked attendance quota for public competition buyers, winner buyers, and performers. Remaining capacity is for direct public orchestra tickets. Pending payments are excluded from confirmed attendance." />
        <Button onClick={() => load().catch(error => message.error(error.message))}>Refresh</Button>
        <Button style={{ margin: '16px 0' }} onClick={() => { setEditing(null); form.resetFields(); setOpen(true); }}>Add session</Button>
        <Table scroll={{ x: 1200 }} rowKey="id" dataSource={event?.orchestraSessions || []} loading={loading} columns={[
            { title: 'Venue', dataIndex: 'venue', render: value => venues.find(venue => venue.id === value)?.label || value },
            { title: 'Date', dataIndex: 'date' }, { title: 'Time', dataIndex: 'time' },
            { title: 'Performance quota', dataIndex: 'complimentaryQuota' },
            { title: 'Paid public', dataIndex: 'paidPublicCount' },
            { title: 'Public holds', dataIndex: 'heldPublicCount' },
            { title: 'Confirmed attendance', dataIndex: 'confirmedAttendance' },
            { title: 'Assigned performance group', render: (_, row) => Number(row.freeSeatingAssigned || 0) },
            { title: 'Legacy allocation', render: (_, row) => Number(row.complimentaryClaimed || 0) },
            { title: 'Actions', render: (_, row) => <Space><Button onClick={() => { setEditing(row); form.setFieldsValue(row); setOpen(true); }}>Edit</Button><Popconfirm title="Delete empty session?" onConfirm={() => update({ deleteSessionId: row.id })}><Button danger disabled={loading}>Delete</Button></Popconfirm></Space> },
        ]} />
        <Modal title="Orchestra session" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} confirmLoading={loading}>
            <Form form={form} layout="vertical" onFinish={values => update({ session: { ...values, ...(editing ? { id: editing.id } : {}) } })}>
                <Form.Item name="venue" label="Venue" rules={[{ required: true }]}><Select onChange={() => form.setFieldsValue({ date: undefined, time: undefined })} options={venues.map(venue => ({ value: venue.id, label: venue.label || venue.id }))} /></Form.Item>
                <Form.Item name="date" label="Date" rules={[{ required: true }]}><Select onChange={() => form.setFieldsValue({ time: undefined })} options={Object.keys(selectedVenue?.sessions || {}).map(value => ({ value, label: value }))} /></Form.Item>
                <Form.Item name="time" label="Time" rules={[{ required: true }]}><Select options={(selectedVenue?.sessions?.[date] || []).map(value => ({ value, label: value }))} /></Form.Item>
                <Form.Item name="complimentaryQuota" label="Performance-linked attendance quota" rules={[{ required: true }]}><InputNumber min={0} precision={0} /></Form.Item>
            </Form>
        </Modal>
    </Card></ConfigProvider>;
}
